import { MenuItem } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import AddEntityModal from "features/entities/AddEntityModal";
import { AddInvestorEntityRelationshipModal } from "features/investors/AddInvestorEntityRelationshipsModal";
import { DeleteInvestorEntityRelationshipsModal } from "features/investors/DeleteInvestorEntityRelationshipsModal";
import { getCapitalActivityTableDataRetrieveQueryKey } from "gen/endpoints/capital-activity/capital-activity";
import { getCommitmentInformationTableDataByFundRetrieveQueryKey } from "gen/endpoints/commitment-information-table-data-by-fund/commitment-information-table-data-by-fund";
import { getCommitmentInformationTableDataForInvestorRetrieveQueryKey } from "gen/endpoints/commitment-information-table-data-for-investor/commitment-information-table-data-for-investor";
import { getCommitmentStatsRetrieveQueryKey } from "gen/endpoints/commitment-stats/commitment-stats";
import { getFeesAndCarryRetrieveQueryKey } from "gen/endpoints/fees-and-carry/fees-and-carry";
import { useInvestorDestroy } from "gen/endpoints/investor/investor";
import { getInvestorEntityListQueryKey } from "gen/endpoints/investor-entity/investor-entity";
import { getInvestorsTableDataRetrieveQueryKey } from "gen/endpoints/investor-table-data/investor-table-data";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { rootRoute } from "shared/navigation/Routes";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export interface InvestorMenuProps {
  onDelete?: () => void;
}

export interface InvestorMenuArgs {
  investorId: string;
}

export interface InvestorMenuReturn {
  getMenuItems: (args: InvestorMenuArgs) => ReactNode;
  openCreateInvestorEntityModal: (args: InvestorMenuArgs) => void;
  openAddExistingInvestorEntityModal: (args: InvestorMenuArgs) => void;
  openDeleteInvestorEntityRelationshipsModal: (args: InvestorMenuArgs) => void;
}

export const useInvestorMenu = ({
  onDelete,
}: InvestorMenuProps = {}): InvestorMenuReturn => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "investor"]);

  const { mutateAsync: deleteInvestor } = useInvestorDestroy({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: getInvestorsTableDataRetrieveQueryKey(),
        });
        onDelete?.();
      },
    },
  });

  const onSuccess = async ({ investorId }: InvestorMenuArgs) => {
    await invalidateQueryKeys(
      queryClient,
      getInvestorsTableDataRetrieveQueryKey(),
      getCommitmentInformationTableDataForInvestorRetrieveQueryKey(investorId),
      getInvestorEntityListQueryKey({ investor_id_exclude: investorId }),
      getInvestorEntityListQueryKey({ investor_id: investorId }),
      getCommitmentInformationTableDataByFundRetrieveQueryKey({
        investor_id: investorId,
      }),
      getCommitmentInformationTableDataForInvestorRetrieveQueryKey(investorId),
      getCommitmentStatsRetrieveQueryKey({ investor_id: investorId }),
      getFeesAndCarryRetrieveQueryKey({ investor_id: investorId }),
      getCapitalActivityTableDataRetrieveQueryKey({ investor_id: investorId }),
    );
    navigate(
      rootRoute({}).investors({}).investor({ investorId }).entities({}).$,
    );
  };

  const openCreateInvestorEntityModal = useConfirmationDialog(AddEntityModal, {
    onSuccess: ({ investorId }) => onSuccess({ investorId: investorId! }),
  });

  const openAddExistingInvestorEntityModal = useConfirmationDialog(
    AddInvestorEntityRelationshipModal,
    { onSuccess },
  );

  const openDeleteInvestorEntityRelationshipsModal = useConfirmationDialog(
    DeleteInvestorEntityRelationshipsModal,
    { onSuccess },
  );

  const confirmInvestorDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<InvestorMenuArgs>,
    {
      title: t("investor:delete.title"),
      description: t("investor:delete.description"),
      successMessage: t("investor:delete.success"),
      performDelete: ({ investorId }) => deleteInvestor({ investorId }),
    },
  );

  const getMenuItems = (args: InvestorMenuArgs) => (
    <>
      <MenuItem onClick={() => openCreateInvestorEntityModal(args)}>
        {t("investor:createInvestorEntity")}
      </MenuItem>
      <MenuItem onClick={() => openAddExistingInvestorEntityModal(args)}>
        {t("investor:addExistingInvestorEntity")}
      </MenuItem>
      <MenuItem
        onClick={() => openDeleteInvestorEntityRelationshipsModal(args)}
      >
        {t("investor:removeExistingInvestorEntity")}
      </MenuItem>
      <MenuItem
        className="destructive"
        onClick={() => confirmInvestorDelete(args)}
      >
        {t("common:delete")}
      </MenuItem>
    </>
  );

  return {
    getMenuItems,
    openCreateInvestorEntityModal,
    openAddExistingInvestorEntityModal,
    openDeleteInvestorEntityRelationshipsModal,
  };
};
